import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import documentationApi from '../../features/api-documentation/services/documentationApi';

// Async thunks
export const fetchDocumentation = createAsyncThunk(
  'apiDocumentation/fetchDoc',
  async ({ entityType, entityId }) => {
    return await documentationApi.getDocumentation(entityType, entityId);
  }
);

export const fetchProjectDocs = createAsyncThunk(
  'apiDocumentation/fetchProjectDocs',
  async (projectId) => {
    return await documentationApi.getByProject(projectId);
  }
);

export const saveDocumentation = createAsyncThunk(
  'apiDocumentation/save',
  async (data) => {
    return await documentationApi.save(data);
  }
);

export const updateDocStatus = createAsyncThunk(
  'apiDocumentation/updateStatus',
  async ({ docId, status }) => {
    return await documentationApi.updateStatus(docId, status);
  }
);

export const fetchDocHistory = createAsyncThunk(
  'apiDocumentation/fetchHistory',
  async (docId) => {
    return await documentationApi.getHistory(docId);
  }
);

export const restoreDocVersion = createAsyncThunk(
  'apiDocumentation/restore',
  async ({ docId, historyId }) => {
    return await documentationApi.restoreVersion(docId, historyId);
  }
);

export const fetchDocCoverage = createAsyncThunk(
  'apiDocumentation/fetchCoverage',
  async (projectId) => {
    return await documentationApi.getCoverage(projectId);
  }
);

export const fetchDocStatuses = createAsyncThunk(
  'apiDocumentation/fetchStatuses',
  async (projectId) => {
    return await documentationApi.getStatuses(projectId);
  }
);

export const generateDocumentation = createAsyncThunk(
  'apiDocumentation/generate',
  async ({ entityType, apiMetadata, context }) => {
    return await documentationApi.generate({ entityType, apiMetadata, context });
  }
);

const documentationSlice = createSlice({
  name: 'apiDocumentation',
  initialState: {
    currentDoc: null,
    projectDocs: [],
    coverage: null,
    docStatuses: {}, // { "entityType:entityId": status }
    history: [],
    isLoading: false,
    isGenerating: false,
    isEditing: false,
    error: null,
  },
  reducers: {
    setEditing: (state, action) => {
      state.isEditing = action.payload;
    },
    clearCurrentDoc: (state) => {
      state.currentDoc = null;
      state.history = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch single doc
      .addCase(fetchDocumentation.pending, (state) => { state.isLoading = true; })
      .addCase(fetchDocumentation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDoc = action.payload;
      })
      .addCase(fetchDocumentation.rejected, (state, action) => {
        state.isLoading = false;
        state.currentDoc = null;
        state.error = action.error.message;
      })
      // Save
      .addCase(saveDocumentation.pending, (state) => {
        state.error = null;
      })
      .addCase(saveDocumentation.fulfilled, (state, action) => {
        state.currentDoc = action.payload;
        state.isEditing = false;
      })
      .addCase(saveDocumentation.rejected, (state, action) => {
        state.error = action.error?.message || 'Failed to save documentation';
        console.error('[DocSlice] saveDocumentation rejected:', action.error?.message);
      })
      // Status update
      .addCase(updateDocStatus.fulfilled, (state, action) => {
        state.currentDoc = action.payload;
      })
      .addCase(updateDocStatus.rejected, (state, action) => {
        state.error = action.error?.message || 'Failed to update status';
        console.error('[DocSlice] updateDocStatus rejected:', action.error?.message);
      })
      // History
      .addCase(fetchDocHistory.fulfilled, (state, action) => {
        state.history = action.payload;
      })
      // Restore
      .addCase(restoreDocVersion.fulfilled, (state, action) => {
        state.currentDoc = action.payload;
      })
      // Coverage
      .addCase(fetchDocCoverage.fulfilled, (state, action) => {
        state.coverage = action.payload;
      })
      // Statuses (for tree)
      .addCase(fetchDocStatuses.fulfilled, (state, action) => {
        const statusMap = {};
        (action.payload || []).forEach(([type, id, status]) => {
          statusMap[`${type}:${id}`] = status;
        });
        state.docStatuses = statusMap;
      })
      // Generate
      .addCase(generateDocumentation.pending, (state) => {
        state.isGenerating = true;
        state.error = null;
      })
      .addCase(generateDocumentation.fulfilled, (state) => {
        state.isGenerating = false;
      })
      .addCase(generateDocumentation.rejected, (state, action) => {
        state.isGenerating = false;
        state.error = action.error?.message || 'Failed to generate documentation';
        console.error('[DocSlice] generateDocumentation rejected:', action.error?.message);
      });
  },
});

export const { setEditing, clearCurrentDoc } = documentationSlice.actions;

// Selectors
export const selectCurrentDoc = (state) => state.apiDocumentation.currentDoc;
export const selectDocCoverage = (state) => state.apiDocumentation.coverage;
export const selectDocStatuses = (state) => state.apiDocumentation.docStatuses;
export const selectDocHistory = (state) => state.apiDocumentation.history;
export const selectDocLoading = (state) => state.apiDocumentation.isLoading;
export const selectDocGenerating = (state) => state.apiDocumentation.isGenerating;
export const selectDocEditing = (state) => state.apiDocumentation.isEditing;
export const selectDocError = (state) => state.apiDocumentation.error;

export default documentationSlice.reducer;
